import { atom } from "jotai";

import { addressApi } from "../services";
import type { AddressCountry, AddressState } from "../types";

export const countriesAtom = atom<AddressCountry[]>([]);

const countriesLoadingAtom = atom(false);

/**
 * Every country, read once and kept. The list is the same on every form and
 * changes about never.
 */
export const loadCountriesAtom = atom(null, async (get, set): Promise<void> => {
  if (get(countriesLoadingAtom) || 0 < get(countriesAtom).length) {
    return;
  }

  set(countriesLoadingAtom, true);

  try {
    set(countriesAtom, await addressApi.fetchCountries());
  } catch {
    // The picker stays on what it had; the field still takes free text.
  } finally {
    set(countriesLoadingAtom, false);
  }
});

/** The states of each country asked for so far, keyed by ISO2 code. */
export const statesAtom = atom<Record<string, AddressState[]>>({});

const statesLoadingAtom = atom<Record<string, true>>({});

export const loadStatesAtom = atom(
  null,
  async (get, set, countryIso2: string): Promise<void> => {
    if (
      "" === countryIso2 ||
      true === get(statesLoadingAtom)[countryIso2] ||
      undefined !== get(statesAtom)[countryIso2]
    ) {
      return;
    }

    set(statesLoadingAtom, (loading) => ({ ...loading, [countryIso2]: true }));

    try {
      const states = await addressApi.fetchStates(countryIso2);

      set(statesAtom, (loaded) => ({ ...loaded, [countryIso2]: states }));
    } catch {
      // A country whose states could not be read offers none, and the field
      // stays a free text one.
      set(statesAtom, (loaded) => ({ ...loaded, [countryIso2]: [] }));
    } finally {
      set(statesLoadingAtom, (loading) =>
        Object.fromEntries(
          Object.entries(loading).filter(([iso2]) => iso2 !== countryIso2),
        ),
      );
    }
  },
);
